import { TPageSettings, TPageSettingsKeyes } from "app/schema/paywallTemplate/pageSettings"
import {
  createContext,
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react"
import { useTemplateJsonContext } from "./JsonProvider"

export enum ECategory {
  General = "General",
  Navbar = "Navbar",
  PurchaseDrawer = "Purchase Drawer",
  Footer = "Footer",
}

type LayoutContext = {
  pageSettings: TPageSettings
  updatePageSetting: (keyValues: { [key in TPageSettingsKeyes]: any }) => void
  selectedCategory: typeof ECategory[keyof typeof ECategory]
  setSelectedCategory: Dispatch<SetStateAction<typeof ECategory[keyof typeof ECategory]>>
}

export const LayoutContext = createContext<LayoutContext>({
  pageSettings: {},
  updatePageSetting: () => {},
  selectedCategory: ECategory.General,
  setSelectedCategory: () => {},
})

export const LayoutProvider: FC<
  PropsWithChildren<{
    paywallId?: number
  }>
> = ({ children }) => {
  const { parsedJson, setJson } = useTemplateJsonContext()
  const [selectedCategory, setSelectedCategory] = useState<
    typeof ECategory[keyof typeof ECategory]
  >(ECategory.General)

  const pageSettings = useMemo(() => parsedJson.pageSettings || {}, [parsedJson])

  const updatePageSetting = useCallback(
    (keyValues) => {
      const newParsedJson = { ...parsedJson, pageSettings: { ...pageSettings, ...keyValues } }
      try {
        setJson(JSON.stringify(newParsedJson, null, 2))
      } catch (e) {
        console.log("Error parsing JSON", e)
      }
    },
    [pageSettings, parsedJson, setJson]
  )

  const value = useMemo(
    () => ({
      pageSettings,
      updatePageSetting,
      selectedCategory,
      setSelectedCategory,
    }),
    [pageSettings, updatePageSetting, selectedCategory, setSelectedCategory]
  )

  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>
}

export const useLayoutContext = () => useContext(LayoutContext)
